import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { BoltIcon } from "../components/Bolts";
import { Send, CheckCircle2, Upload, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_IMAGES = 3;

const CustomRequestPage = () => {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", address: "",
        description: "", length_in: "", width_in: "",
    });
    const [images, setImages] = useState([]); // [{file, url}]
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onPickImages = (e) => {
        const incoming = Array.from(e.target.files || []);
        const room = MAX_IMAGES - images.length;
        if (room <= 0) {
            toast.error(`You can upload up to ${MAX_IMAGES} photos.`);
            return;
        }
        const accepted = incoming.slice(0, room).map((file) => ({ file, url: URL.createObjectURL(file) }));
        setImages((prev) => [...prev, ...accepted]);
        if (incoming.length > room) toast.message(`Only the first ${MAX_IMAGES} photos are kept.`);
        e.target.value = "";
    };

    const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.description.trim()) {
            toast.error("Please fill in at least your name, email, and a description.");
            return;
        }
        setSubmitting(true);
        try {
            const data = new FormData();
            data.append("name", form.name.trim());
            data.append("email", form.email.trim());
            data.append("description", form.description.trim());
            if (form.phone.trim()) data.append("phone", form.phone.trim());
            if (form.address.trim()) data.append("address", form.address.trim());
            if (form.length_in.trim()) data.append("length_in", form.length_in.trim());
            if (form.width_in.trim()) data.append("width_in", form.width_in.trim());
            images.forEach((img) => data.append("images", img.file));

            await axios.post(`${API}/custom-requests`, data, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            setSuccess(true);
            toast.success("Request sent!");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Could not send right now. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Send failed.");
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div data-testid="custom-request-success" className="min-h-[70vh] flex items-center justify-center container-x py-20">
                <div className="text-center max-w-md">
                    <CheckCircle2 className="w-14 h-14 text-storm-bolt mx-auto mb-5" />
                    <h1 className="text-3xl md:text-4xl text-white mb-4">Request received!</h1>
                    <p className="text-storm-mist">
                        Thanks — we've got your custom request and will be in touch by email soon to talk details and pricing. Every piece is handmade to order, so we'll make sure we get it exactly right.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="custom-request-page" className="container-x py-16 md:py-24 max-w-2xl">
            <p className="eyebrow flex items-center gap-2 mb-3">
                <BoltIcon className="w-3.5 h-3.5" /> Custom Request
            </p>
            <h1 className="text-4xl md:text-5xl text-white mb-4">Tell us what you want made.</h1>
            <p className="text-storm-mist mb-10 leading-relaxed">
                Got a one-of-a-kind idea? Fill this out with as much detail as you can — add photos or references, rough dimensions, whatever helps. We'll follow up by email to nail down the details and price.
            </p>

            <div className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Name *">
                        <Input name="name" value={form.name} onChange={onChange} data-testid="cr-name"
                            className="bg-storm-void border-white/10 text-white rounded-none focus-visible:ring-storm-bolt focus-visible:border-storm-bolt" />
                    </Field>
                    <Field label="Email *">
                        <Input name="email" type="email" value={form.email} onChange={onChange} data-testid="cr-email"
                            className="bg-storm-void border-white/10 text-white rounded-none focus-visible:ring-storm-bolt focus-visible:border-storm-bolt" />
                    </Field>
                    <Field label="Phone">
                        <Input name="phone" value={form.phone} onChange={onChange} data-testid="cr-phone"
                            className="bg-storm-void border-white/10 text-white rounded-none focus-visible:ring-storm-bolt focus-visible:border-storm-bolt" />
                    </Field>
                    <Field label="Address">
                        <Input name="address" value={form.address} onChange={onChange} data-testid="cr-address"
                            className="bg-storm-void border-white/10 text-white rounded-none focus-visible:ring-storm-bolt focus-visible:border-storm-bolt" />
                    </Field>
                </div>

                <Field label="Brief description *">
                    <Textarea name="description" value={form.description} onChange={onChange} rows={4} data-testid="cr-description"
                        placeholder="What are you imagining? Materials, colors, text, vibe…"
                        className="bg-storm-void border-white/10 text-white rounded-none resize-none focus-visible:ring-storm-bolt focus-visible:border-storm-bolt" />
                </Field>

                <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Length (inches)">
                        <Input name="length_in" value={form.length_in} onChange={onChange} inputMode="decimal" data-testid="cr-length"
                            placeholder="e.g. 12"
                            className="bg-storm-void border-white/10 text-white rounded-none focus-visible:ring-storm-bolt focus-visible:border-storm-bolt" />
                    </Field>
                    <Field label="Width (inches)">
                        <Input name="width_in" value={form.width_in} onChange={onChange} inputMode="decimal" data-testid="cr-width"
                            placeholder="e.g. 8"
                            className="bg-storm-void border-white/10 text-white rounded-none focus-visible:ring-storm-bolt focus-visible:border-storm-bolt" />
                    </Field>
                </div>

                <Field label={`Photos / references (up to ${MAX_IMAGES})`}>
                    <div className="flex flex-wrap gap-3">
                        {images.map((img, i) => (
                            <div key={i} className="relative w-24 h-24 border border-white/10">
                                <img src={img.url} alt="" className="w-full h-full object-cover" />
                                <button type="button" onClick={() => removeImage(i)}
                                    className="absolute -top-2 -right-2 bg-storm-void border border-white/20 rounded-full p-0.5 text-white hover:text-storm-bolt"
                                    data-testid={`cr-remove-img-${i}`}>
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                        {images.length < MAX_IMAGES && (
                            <label className="w-24 h-24 border border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer text-storm-mist hover:border-storm-bolt hover:text-storm-bolt transition-colors">
                                <Upload className="w-5 h-5 mb-1" />
                                <span className="text-[10px] uppercase tracking-wider">Add</span>
                                <input type="file" accept="image/*" multiple onChange={onPickImages} className="hidden" data-testid="cr-images" />
                            </label>
                        )}
                    </div>
                </Field>

                <button onClick={onSubmit} disabled={submitting} data-testid="cr-submit"
                    className="btn-primary justify-center w-full md:w-auto disabled:opacity-60">
                    {submitting ? "Sending…" : <><Send className="w-4 h-4" /> Send request</>}
                </button>
            </div>
        </div>
    );
};

const Field = ({ label, children }) => (
    <div>
        <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">{label}</label>
        {children}
    </div>
);

export default CustomRequestPage;
